import React, {useState, useMemo, useCallback} from 'react'
import styled from 'styled-components'
import {
  IMAGE_ASSET,
  SERVICE_CANCELLATION_SEARCH_REGENCY,
  SERVICE_CANCELLED_RESPONSE,
} from 'consts'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {useTheme} from 'themes'
import {TreeLocationNameResponse} from 'types'
import {useHistory} from 'utils'
import {Icon, Image, Input, ListLazy, Paragraph} from 'common/components'
import {useDispatch} from 'lib/redux'
import convertUnit from 'lib/unit'
import {GiftshopFilterRegencyListItem} from './GiftshopFilterRegencyListItem'

const StyledInputContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: ${convertUnit(12)};
`

const StyledSearchContainer = styled(Input)`
  padding: ${convertUnit(20)};
`

const StyledContentContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`

const StyledEmptyContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const StyledEmptyImage = styled(Image)`
  width: 120;
  aspect-ratio: 1;
  margin-bottom: 5;
`

const StyledEmptyTitle = styled(Paragraph)`
  text-align: center;
`

const StyledEmptyDescription = styled(Paragraph)`
  margin-bottom: 20;
  text-align: center;
`

export function GiftshopFilterRegencyList() {
  const theme = useTheme()
  const {translate} = useTranslation()
  const stateData = useState<ReadonlyArray<TreeLocationNameResponse>>([])
  const [search, setSearch] = useState<{
    query: string
    lastQueryChange: number
  }>({
    query: '',
    lastQueryChange: 0,
  })
  const history = useHistory()
  const {update} = useDispatch()

  const [input, setInput] = useState('')

  const handleKeyExtractor = useCallback(
    (item: TreeLocationNameResponse) => item.id,
    [],
  )

  const handleSelectItem = useCallback(
    (item?: TreeLocationNameResponse) => {
      update('exploreFilter', {
        regency: item,
      })
      update('giftshopState', {
        exploreRefresher: new Date(),
      })
      history.goBack()
    },
    [history, update],
  )

  const handleRenderItem = useCallback(
    (item: TreeLocationNameResponse) => (
      <GiftshopFilterRegencyListItem
        name={item.name}
        onSelect={() => handleSelectItem(item)}
      />
    ),
    [handleSelectItem],
  )

  const handleRenderPinnedItem = useMemo(
    () => (
      <GiftshopFilterRegencyListItem
        name={translate('giftShop:exploreFilterRegencyAllCities')}
        style={{
          borderTop: `${convertUnit(1)} solid ${theme.gray_1}`,
          borderBottom: `${convertUnit(1)} solid ${theme.gray_1}`,
          padding: `${convertUnit(8)} ${convertUnit(20)}`,
          margin: 0,
        }}
        onSelect={() => {
          handleSelectItem(undefined)
        }}
      />
    ),
    [handleSelectItem, theme.gray_1, translate],
  )

  const handleRenderEmpty = useMemo(
    () => (
      <StyledEmptyContainer>
        <StyledEmptyImage
          src={IMAGE_ASSET('giftshop', 'not-found.png')}
          alt="empty"
        />
        <StyledEmptyTitle fontSize="l" fontWeight="bold">
          {translate('giftShop:exploreFilterRegencyEmptyTitle')}
        </StyledEmptyTitle>
        <StyledEmptyDescription color="gray_5">
          {translate('giftShop:exploreFilterRegencyEmptySubtitle')}
        </StyledEmptyDescription>
      </StyledEmptyContainer>
    ),
    [translate],
  )

  const handleLoadData = useCallback(async () => {
    const response = await requestData('tree_get_location_regency', {
      params: {
        q: search.query,
      },
      cancelId: SERVICE_CANCELLATION_SEARCH_REGENCY,
    })

    if (
      typeof response === 'string' &&
      response === SERVICE_CANCELLED_RESPONSE
    ) {
      return undefined
    }

    return typeof response !== 'string' && response.status === 200
      ? response.data.result
      : null
  }, [search.query])

  const handleRenderList = useMemo(
    () => (
      <ListLazy
        search={search.lastQueryChange}
        loadData={handleLoadData}
        stateData={stateData}
        keyExtractor={handleKeyExtractor}
        renderItem={handleRenderItem}
        listEmptyElement={handleRenderEmpty}
        style={{
          margin: `${convertUnit(8)} ${convertUnit(20)}`,
        }}
      />
    ),
    [
      handleKeyExtractor,
      handleLoadData,
      handleRenderEmpty,
      handleRenderItem,
      search.lastQueryChange,
      stateData,
    ],
  )

  const handleUpdateSearchByTitleQuery = useCallback(
    (newQuery: string) => {
      setInput(newQuery)
      const nextStatus = (() => {
        if (newQuery.length === 0) return 'initial'
        if (newQuery.length < 3) return 'invalid-input'
        return 'fetching'
      })()
      setSearch((prev) => ({
        ...prev,
        query: newQuery,
        status: nextStatus,
        lastQueryChange: new Date().getTime(),
      }))
    },
    [setSearch],
  )

  const handleResetSearch = useCallback(() => {
    setInput('')
    setSearch({
      query: '',
      lastQueryChange: new Date().getTime(),
    })
  }, [])

  const handleRenderTextInput = useMemo(
    () => (
      <StyledSearchContainer
        value={input}
        containerStyle={{
          padding: convertUnit(20),
          width: '100%',
        }}
        onChangeText={(searchQuery) => {
          handleUpdateSearchByTitleQuery(searchQuery)
        }}
        placeholder={translate('giftShop:exploreFilterRegencyPlaceholder')}
        // containerStyle={styles.searchContainer}
        rightIcon={
          input === '' ? undefined : <Icon type="close" color="gray_3" />
        }
        onRightIconClick={handleResetSearch}
      />
    ),
    [handleResetSearch, handleUpdateSearchByTitleQuery, input, translate],
  )

  const handleRenderContent = useMemo(
    () => (
      <StyledContentContainer>
        {handleRenderPinnedItem}
        {handleRenderList}
      </StyledContentContainer>
    ),
    [handleRenderList, handleRenderPinnedItem],
  )

  return (
    <>
      <StyledInputContainer>{handleRenderTextInput}</StyledInputContainer>
      {handleRenderContent}
    </>
  )
}
